import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Modal } from 'antd';
import { message } from '$/components/Message';
import IconFont from '$/components/IconFont';
import type { OfficialAssetServerMaterial } from '$/api/server.types';
import { deleteNodeExecuteTask } from '$/domains/node/nodeService';
import { uploadFileBeforeEditor } from '$/utils/upload';
import { useLanguageStore } from '$/store/languageStore';
import RadishCostText from '$/pages/RoboneoTeamFlow/components/RadishCostText';
import radishIcon from '$/components/CalcRadish/radish.png';
import HistoryModalContent from '../../HistoryModalContent';
import type { AssetModalInsertTarget, AssetModalListItem } from '../../type';
import { resolveRoomIdByInsertTarget } from '../../utils';
import OfficialMaterialSelectModal from './OfficialMaterialSelectModal';
import RoleSettingForm, {
  type RoleGenMaterialCategory,
  type RoleGenMaterialFilter,
  type RoleGenModelParameterValue,
  type RoleGenFormValues,
} from './RoleSettingForm';
import RoleModelControls from './RoleModelControls';
import ThreeViewPreview from './ThreeViewPreview';
import HistoryList, { type RoleGenHistoryItem } from './HistoryList';
import useModelOptions from './useModelOptions';
import useRoleGenSubmit, { type ThreeViewImage } from './useRoleGenSubmit';
import useRoleGenCost from './useRoleGenCost';
import useRoleGenHistory from './useRoleGenHistory';
import { ROLE_GEN_BUSINESS_TYPE } from './roleGenerateTask';
import {
  buildMaterialValuePatch,
  createInitialRoleGenValues,
  historyItemToRoleMaterialPayload,
  loadRoleGenOfficialMaterialPatch,
  prepareRoleGenSubmitValues,
} from './roleMaterialUtils';
import {
  roleGenMaterialCategoryToUploadLocation,
  trackRoboneoAssetRoleSaveEnterClick,
  trackRoboneoAssetRoleShow,
  trackRoboneoAssetRoleUploadControlClick,
  type RoleAssetUploadLocation,
  type RoleAssetUploadTriggerType,
} from '../../tracking';
import styles from './index.module.scss';

/** 创建角色弹窗需高于资产库弹层及其二级业务弹层，低于全局 toast。 */
const CREATE_ROLE_MODAL_Z_INDEX = 5600;
const CREATE_ROLE_HISTORY_MODAL_Z_INDEX = 5700;
type RoleGenHistoryUploadTarget = RoleGenMaterialCategory | 'reference';

/** 从资产卡片重新编辑角色时传入的表单和历史任务定位上下文。 */
export interface RoleGenEditContext {
  taskId: string;
  formValues: RoleGenFormValues;
}

export interface CreateRoleModalProps {
  open: boolean;
  insertTarget?: AssetModalInsertTarget;
  initialEditContext?: RoleGenEditContext | null;
  onClose: () => void;
  /** 存为资产回调：父组件复用资产库现有保存流程并弹出目录选择。 */
  onSaveImageAsAsset?: (image: ThreeViewImage, formSnapshot: RoleGenFormValues, taskId: string) => Promise<void> | void;
  /** 添加到画布回调：父组件复用个人画布或团队画布既有插入流程。 */
  onAddImageToCanvas?: (image: ThreeViewImage, formSnapshot: RoleGenFormValues) => Promise<void> | void;
  /** 团队画布不可编辑时禁用添加入口。 */
  addToCanvasDisabledReason?: string;
}

/** 将当前任务时间格式化为历史记录展示文案。 */
function formatRoleGenHistoryTime(timestamp: number, todayText: string): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${todayText} ${hour}:${minute}`;
}

/**
 * 校验步骤 1 表单是否满足生成按钮可点条件，角色名称和描述必填，参考素材均为选填。
 *
 * @param values 当前表单值
 * @returns true 表示可提交
 */
function isFormValid(values: RoleGenFormValues): boolean {
  return (
    values.name.trim().length > 0
    && values.prompt.trim().length > 0
    && values.modelId.length > 0
  );
}

/** 仅在素材 id 未被用户改动时合并异步详情，避免旧请求覆盖新选择。 */
function mergeRoleGenOfficialMaterialPatch(
  currentValues: RoleGenFormValues,
  sourceValues: RoleGenFormValues,
  materialPatch: Partial<RoleGenFormValues>,
): RoleGenFormValues {
  return {
    ...currentValues,
    ...(currentValues.clothingMaterialId === sourceValues.clothingMaterialId
      && materialPatch.clothingOfficialMaterial
      ? {
        clothingMaterialId: materialPatch.clothingMaterialId ?? currentValues.clothingMaterialId,
        clothingMaterialName: materialPatch.clothingMaterialName,
        clothingMaterialCoverUrl: materialPatch.clothingMaterialCoverUrl,
        clothingMaterialSource: materialPatch.clothingMaterialSource,
        clothingOfficialMaterial: materialPatch.clothingOfficialMaterial,
        clothingMaterialFilter: materialPatch.clothingMaterialFilter,
      }
      : {}),
    ...(currentValues.hairstyleMaterialId === sourceValues.hairstyleMaterialId
      && materialPatch.hairstyleOfficialMaterial
      ? {
        hairstyleMaterialId: materialPatch.hairstyleMaterialId,
        hairstyleMaterialName: materialPatch.hairstyleMaterialName,
        hairstyleMaterialCoverUrl: materialPatch.hairstyleMaterialCoverUrl,
        hairstyleMaterialSource: materialPatch.hairstyleMaterialSource,
        hairstyleOfficialMaterial: materialPatch.hairstyleOfficialMaterial,
        hairstyleMaterialFilter: materialPatch.hairstyleMaterialFilter,
      }
      : {}),
    ...(currentValues.accessoryMaterialId === sourceValues.accessoryMaterialId
      && materialPatch.accessoryOfficialMaterial
      ? {
        accessoryMaterialId: materialPatch.accessoryMaterialId,
        accessoryMaterialName: materialPatch.accessoryMaterialName,
        accessoryMaterialCoverUrl: materialPatch.accessoryMaterialCoverUrl,
        accessoryMaterialSource: materialPatch.accessoryMaterialSource,
        accessoryOfficialMaterial: materialPatch.accessoryOfficialMaterial,
        accessoryMaterialFilter: materialPatch.accessoryMaterialFilter,
      }
      : {}),
  };
}

/**
 * 角色创建弹窗；模型、素材、生成和历史均走真实接口链路。
 * 关闭时统一清理内部 state，再次打开重新初始化默认模型。
 *
 * @param props 见 CreateRoleModalProps
 * @returns 弹窗节点
 */
function CreateRoleModal({
  open,
  insertTarget = 'canvas',
  initialEditContext,
  onClose,
  onSaveImageAsAsset,
  onAddImageToCanvas,
  addToCanvasDisabledReason,
}: CreateRoleModalProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const [values, setValues] = useState<RoleGenFormValues>(() => createInitialRoleGenValues());
  const [selectingMaterialCategory, setSelectingMaterialCategory] = useState<RoleGenMaterialCategory | null>(null);
  const [historyUploadTarget, setHistoryUploadTarget] = useState<RoleGenHistoryUploadTarget | null>(null);
  const [uploadingReferenceImage, setUploadingReferenceImage] = useState(false);
  const [uploadingMaterialCategory, setUploadingMaterialCategory] = useState<RoleGenMaterialCategory | null>(null);
  const [mergingOfficialMaterials, setMergingOfficialMaterials] = useState(false);
  const [selectedHistoryPreview, setSelectedHistoryPreview] = useState<RoleGenHistoryItem | null>(null);
  const [deletedHistoryTaskIds, setDeletedHistoryTaskIds] = useState<Set<string>>(() => new Set());
  const openRef = useRef(open);
  const initialEditAppliedRef = useRef(false);
  const formHydrationVersionRef = useRef(0);
  const {
    options: modelOptions, loading: modelOptionsLoading,
  } = useModelOptions();
  const selectedModel = modelOptions.find((item) => item.modelId === values.modelId);
  const roomId = useMemo(() => resolveRoomIdByInsertTarget(insertTarget), [insertTarget]);
  const formValid = isFormValid(values);
  const costEnabled = open && formValid && Boolean(selectedModel?.apiName);
  const {
    cost: roleGenCost,
    discountCost: roleGenDiscountCost,
    loading: roleGenCostLoading,
  } = useRoleGenCost({
    roomId,
    values,
    selectedModel,
    enabled: costEnabled,
  });
  const {
    status, previewImages, taskId, submittedAt, submit, reset: resetSubmit,
  } = useRoleGenSubmit({ roomId, selectedModel });
  const {
    items: roleGenHistoryItems,
    initialized: roleGenHistoryInitialized,
    refresh: refreshRoleGenHistory,
  } = useRoleGenHistory({ open, roomId });

  // 历史展示和删除后的相邻项选择共用同一份可见数据，排除失败及已删除任务。
  const visibleRoleGenHistoryItems = useMemo(
    () => roleGenHistoryItems.filter(
      (item) => !item.failed && !deletedHistoryTaskIds.has(item.taskId),
    ),
    [deletedHistoryTaskIds, roleGenHistoryItems],
  );
  const selectedMaterialFilter = selectingMaterialCategory
    ? {
      clothing: values.clothingMaterialFilter,
      hairstyle: values.hairstyleMaterialFilter,
      accessory: values.accessoryMaterialFilter,
    }[selectingMaterialCategory]
    : undefined;

  const creatingTask = mergingOfficialMaterials || (status === 'generating' && !taskId);

  /** 创建角色上传入口点击或拖拽时，统一补充当前 room_id。 */
  const handleRoleUploadControlClick = useCallback((
    location: RoleAssetUploadLocation,
    triggerType: RoleAssetUploadTriggerType,
  ): void => {
    trackRoboneoAssetRoleUploadControlClick({
      roomId,
      location,
      triggerType,
    });
  }, [roomId]);

  /** 关闭创建角色弹框时清空所有临时选择、上传状态和生成预览。 */
  const resetRoleModalState = useCallback((): void => {
    formHydrationVersionRef.current += 1;
    setValues(createInitialRoleGenValues());
    setSelectingMaterialCategory(null);
    setHistoryUploadTarget(null);
    setUploadingReferenceImage(false);
    setUploadingMaterialCategory(null);
    setMergingOfficialMaterials(false);
    setSelectedHistoryPreview(null);
    setDeletedHistoryTaskIds(new Set());
    initialEditAppliedRef.current = false;
    resetSubmit();
  }, [resetSubmit]);

  /** 关闭弹框前先清理内部状态，保证再次打开不带入上次选择。 */
  const handleClose = useCallback((): void => {
    openRef.current = false;
    resetRoleModalState();
    onClose();
  }, [onClose, resetRoleModalState]);

  /** 合并子表单回传的局部值，创建角色弹框统一持有完整表单快照。 */
  const handleChange = useCallback((patch: Partial<RoleGenFormValues>) => {
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  /** 回填角色快照后按官方素材 id 异步补全详情，并忽略弹框关闭或后续回填产生的过期结果。 */
  const applyRoleGenFormSnapshot = useCallback((formSnapshot: RoleGenFormValues): void => {
    const hydrationVersion = formHydrationVersionRef.current + 1;
    formHydrationVersionRef.current = hydrationVersion;
    setValues({
      ...formSnapshot,
      modelParameters: { ...formSnapshot.modelParameters },
    });
    loadRoleGenOfficialMaterialPatch(formSnapshot).then((materialPatch) => {
      if (
        !openRef.current
        || formHydrationVersionRef.current !== hydrationVersion
        || Object.keys(materialPatch).length === 0
      ) {
        return;
      }
      setValues((prev) => mergeRoleGenOfficialMaterialPatch(
        prev,
        formSnapshot,
        materialPatch,
      ));
    }).catch(() => undefined);
  }, []);

  /** 打开指定素材类型的官方资产选择弹框。 */
  const handleOpenMaterialSelector = useCallback((category: RoleGenMaterialCategory): void => {
    setSelectingMaterialCategory(category);
  }, []);

  /** 关闭官方资产选择弹框，保留创建角色表单当前输入。 */
  const handleCloseMaterialSelector = useCallback((): void => {
    setSelectingMaterialCategory(null);
  }, []);

  /** 选中官方素材后回填素材数据与筛选上下文，供重新打开和历史编辑时恢复定位。 */
  const handleConfirmMaterial = useCallback((
    material: OfficialAssetServerMaterial,
    filter: RoleGenMaterialFilter,
  ): void => {
    if (!selectingMaterialCategory) return;
    setValues((prev) => ({
      ...prev,
      ...buildMaterialValuePatch(selectingMaterialCategory, {
        id: String(material.id),
        name: material.name,
        coverUrl: material.main_image.url,
        source: 'official',
        officialMaterial: material,
        filter,
      }),
    }));
    setSelectingMaterialCategory(null);
  }, [selectingMaterialCategory]);

  /** 上传角色参考图，成功后将线上 URL 回填到表单供生成任务使用。 */
  const handleUploadReferenceImage = useCallback(async (file: File): Promise<void> => {
    if (!file.type.startsWith('image/')) {
      message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_file_toast1));
      return;
    }
    setUploadingReferenceImage(true);
    try {
      const [uploadedImage] = await uploadFileBeforeEditor([file], undefined, 'role_reference');
      if (!uploadedImage?.url) {
        throw new Error('Role reference image upload missing url');
      }
      if (!openRef.current) return;
      setValues((prev) => ({
        ...prev,
        referenceImageUrl: uploadedImage.url,
        referenceImageSource: 'upload',
      }));
    } catch {
      message.error(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_upload_fail_text));
    } finally {
      setUploadingReferenceImage(false);
    }
  }, [languageStore]);

  /** 本地上传参考素材，复用项目上传工具获取线上 URL 后回填当前素材行。 */
  const handleLocalUploadMaterial = useCallback(async (
    category: RoleGenMaterialCategory,
    file: File,
  ): Promise<void> => {
    if (!file.type.startsWith('image/')) {
      message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_file_toast1));
      return;
    }
    setUploadingMaterialCategory(category);
    try {
      const [uploadedImage] = await uploadFileBeforeEditor(
        [file],
        undefined,
        roleGenMaterialCategoryToUploadLocation(category),
      );
      if (!uploadedImage?.url) {
        throw new Error('Role material upload missing url');
      }
      if (!openRef.current) return;
      setValues((prev) => ({
        ...prev,
        ...buildMaterialValuePatch(category, {
          id: String(uploadedImage.id || uploadedImage.url),
          name: uploadedImage.name || file.name,
          coverUrl: uploadedImage.url,
          source: 'upload',
        }),
      }));
    } catch {
      message.error(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_upload_fail_text));
    } finally {
      setUploadingMaterialCategory((prev) => (prev === category ? null : prev));
    }
  }, [languageStore]);

  /** 打开历史记录选择弹框，选择完成后回填到当前素材行。 */
  const handleOpenHistoryUpload = useCallback((category: RoleGenMaterialCategory): void => {
    setHistoryUploadTarget(category);
  }, []);

  /** 打开角色参考图的历史记录选择弹框。 */
  const handleOpenReferenceHistoryUpload = useCallback((): void => {
    setHistoryUploadTarget('reference');
  }, []);

  /** 关闭历史记录选择弹框，保留创建角色表单当前输入。 */
  const handleCloseHistoryUpload = useCallback((): void => {
    setHistoryUploadTarget(null);
  }, []);

  /** 适配历史记录组件的弹层开关回调，只在其请求关闭时关闭当前二级弹框。 */
  const handleHistoryPopoverOpenChange = useCallback((nextOpen: boolean): void => {
    if (!nextOpen) {
      handleCloseHistoryUpload();
    }
  }, [handleCloseHistoryUpload]);

  /** 选择单张历史记录图片后回填当前行参考素材。 */
  const handleConfirmHistoryUpload = useCallback((selectedItem: AssetModalListItem): void => {
    if (!historyUploadTarget || !selectedItem) return;
    if (historyUploadTarget === 'reference') {
      setValues((prev) => ({
        ...prev,
        referenceImageUrl: selectedItem.mediaUrl || selectedItem.coverUrl,
        referenceImageSource: 'history',
      }));
      setHistoryUploadTarget(null);
      return;
    }
    setValues((prev) => ({
      ...prev,
      ...buildMaterialValuePatch(historyUploadTarget, historyItemToRoleMaterialPayload(selectedItem)),
    }));
    setHistoryUploadTarget(null);
  }, [historyUploadTarget]);

  /** 切换角色生成模型时同步写入模型、工具与默认参数，避免旧模型参数串到新模型。 */
  const handleModelChange = useCallback((modelKey: string): void => {
    const nextModel = modelOptions.find((item) => item.key === modelKey);
    if (!nextModel) return;
    setValues((prev) => ({
      ...prev,
      modelId: nextModel.modelId,
      modelApiName: nextModel.apiName,
      nodeModelId: nextModel.nodeModelId,
      modelParameters: { ...nextModel.parameters },
    }));
  }, [modelOptions]);

  /** 更新当前角色生成模型参数，只写入底部配置显式选择值。 */
  const handleModelParameterChange = useCallback((key: string, value: RoleGenModelParameterValue): void => {
    setValues((prev) => ({
      ...prev,
      modelParameters: {
        ...prev.modelParameters,
        [key]: value,
      },
    }));
  }, []);

  /** 提交创建角色任务，仅在素材准备和任务创建阶段阻止重复触发。 */
  const handleSubmit = useCallback(async () => {
    if (!formValid || creatingTask) return;
    setSelectedHistoryPreview(null);
    setMergingOfficialMaterials(true);
    try {
      const materialPatch = await loadRoleGenOfficialMaterialPatch(values);
      const hydratedValues = mergeRoleGenOfficialMaterialPatch(values, values, materialPatch);
      const { displayValues, submitValues } = await prepareRoleGenSubmitValues(hydratedValues);
      if (!openRef.current) return;
      setValues((prev) => mergeRoleGenOfficialMaterialPatch(prev, values, materialPatch));
      setMergingOfficialMaterials(false);
      await submit(submitValues, displayValues);
    } catch {
      message.error(languageStore.smartcatTranslationText((v) => v.roboneo_web_failed));
    } finally {
      setMergingOfficialMaterials(false);
    }
  }, [creatingTask, formValid, languageStore, submit, values]);

  /** 点击历史记录时替换右侧预览，当前生成任务复用实时状态，其他生成中任务展示 loading。 */
  const handlePreviewHistoryItem = useCallback((item: RoleGenHistoryItem): void => {
    const isCurrentGeneratingTask = status === 'generating'
      && (item.taskId === taskId || item.taskId === 'role-generating');
    setSelectedHistoryPreview(isCurrentGeneratingTask ? null : item);
  }, [status, taskId]);

  /** 点击重新编辑时仅回填左侧表单，不改变右侧历史选中预览。 */
  const handleEditHistoryItem = useCallback((item: RoleGenHistoryItem): void => {
    if (item.loading) return;
    applyRoleGenFormSnapshot(item.formSnapshot);
  }, [applyRoleGenFormSnapshot]);

  /** 删除历史项后，当前预览按列表顺序优先切换到后一项，其次切换到前一项。 */
  const handleDeleteHistoryItem = useCallback(async (item: RoleGenHistoryItem): Promise<void> => {
    try {
      await deleteNodeExecuteTask([item.taskId], ROLE_GEN_BUSINESS_TYPE);
      if (!openRef.current) return;
      const deletedIndex = visibleRoleGenHistoryItems.findIndex(
        (historyItem) => historyItem.taskId === item.taskId,
      );
      const fallbackItem = deletedIndex < 0
        ? null
        : visibleRoleGenHistoryItems[deletedIndex + 1]
          ?? visibleRoleGenHistoryItems[deletedIndex - 1]
          ?? null;
      setDeletedHistoryTaskIds((prev) => new Set(prev).add(item.taskId));
      setSelectedHistoryPreview((prev) => (
        prev?.taskId === item.taskId ? fallbackItem : prev
      ));
      message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_delete_success));
    } catch {
      message.error(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_delete_fail));
    }
  }, [languageStore, visibleRoleGenHistoryItems]);

  /** 将指定角色结果交给弹框宿主保存，并统一上报保存入口埋点。 */
  const saveRoleImageAsAsset = useCallback(async (
    image: ThreeViewImage,
    formSnapshot: RoleGenFormValues,
    saveTaskId: string,
  ): Promise<void> => {
    if (!onSaveImageAsAsset) {
      return;
    }
    trackRoboneoAssetRoleSaveEnterClick({
      roomId,
      taskId: saveTaskId,
      mediaUrl: image.url,
    });
    await onSaveImageAsAsset(image, formSnapshot, saveTaskId);
  }, [onSaveImageAsAsset, roomId]);

  /** 保存当前预览角色，表单快照与任务 id 跟随当前预览来源。 */
  const handleSaveImage = useCallback(async (image: ThreeViewImage): Promise<void> => {
    await saveRoleImageAsAsset(
      image,
      selectedHistoryPreview?.formSnapshot ?? values,
      selectedHistoryPreview?.taskId ?? taskId,
    );
  }, [saveRoleImageAsAsset, selectedHistoryPreview, taskId, values]);

  /** 从历史卡片菜单保存角色，始终使用该卡片自己的结果和表单快照。 */
  const handleSaveHistoryItem = useCallback(async (item: RoleGenHistoryItem): Promise<void> => {
    const image = item.images[0];
    if (!image) return;
    await saveRoleImageAsAsset(image, item.formSnapshot, item.taskId);
  }, [saveRoleImageAsAsset]);

  /** 将当前预览图及其对应表单快照交给宿主添加到画布。 */
  const handleAddImageToCanvas = useCallback(async (image: ThreeViewImage) => {
    if (!onAddImageToCanvas) return;
    const formSnapshot = selectedHistoryPreview?.formSnapshot ?? values;
    await onAddImageToCanvas(image, formSnapshot);
  }, [onAddImageToCanvas, selectedHistoryPreview, values]);

  /** 历史列表只在生成中插入本地占位，成功后完全使用后端历史避免重复和名称来源不一致。 */
  const generatedHistory = useMemo<RoleGenHistoryItem[]>(() => {
    const createdAt = submittedAt || Date.now();
    const displayTime = formatRoleGenHistoryTime(
      createdAt,
      languageStore.smartcatTranslationText((value) => value['3']),
    );

    if (status === 'generating') {
      const currentItem = {
        taskId: taskId || 'role-generating',
        createdAt,
        modelId: values.modelId,
        images: [],
        formSnapshot: values,
        displayName: values.name.trim(),
        displayTime,
        loading: true,
      };
      return [
        currentItem,
        ...visibleRoleGenHistoryItems.filter((item) => item.taskId !== currentItem.taskId),
      ];
    }

    return visibleRoleGenHistoryItems;
  }, [languageStore, status, submittedAt, taskId, values, visibleRoleGenHistoryItems]);

  const submitDisabled = !formValid
    || creatingTask
    || !roomId
    || !selectedModel?.apiName;
  const submitLabel = languageStore.smartcatTranslationText((value) => value.create);
  const showCreditCost = costEnabled || roleGenCostLoading || roleGenCost !== undefined;
  const currentSubmitDeleted = Boolean(taskId && deletedHistoryTaskIds.has(taskId));
  let displayPreviewStatus = currentSubmitDeleted ? 'idle' : status;
  let displayPreviewImages = currentSubmitDeleted || status === 'generating' ? [] : previewImages;
  let selectedHistoryTaskId: string | undefined;
  if (!currentSubmitDeleted && status === 'generating') {
    selectedHistoryTaskId = taskId || 'role-generating';
  }
  if (selectedHistoryPreview) {
    displayPreviewStatus = selectedHistoryPreview.loading ? 'generating' : 'succeeded';
    displayPreviewImages = selectedHistoryPreview.images;
    selectedHistoryTaskId = selectedHistoryPreview.taskId;
  }

  // 模型列表加载后，无模型或回显模型已下架时统一选中第一项并恢复其默认参数。
  useEffect(() => {
    if (
      !open
      || modelOptions.length === 0
      || modelOptions.some((item) => item.modelId === values.modelId)
    ) {
      return;
    }
    setValues((prev) => {
      if (modelOptions.some((item) => item.modelId === prev.modelId)) return prev;
      const defaultModel = modelOptions[0];
      return {
        ...prev,
        modelId: defaultModel.modelId,
        modelApiName: defaultModel.apiName,
        nodeModelId: defaultModel.nodeModelId,
        modelParameters: { ...defaultModel.parameters },
      };
    });
  }, [modelOptions, open, values.modelId]);

  // 从资产卡片重新编辑时先恢复左侧参数，历史预览等待列表请求完成后再定位。
  useEffect(() => {
    if (!open) {
      initialEditAppliedRef.current = false;
      return;
    }
    if (!initialEditContext) {
      initialEditAppliedRef.current = true;
      return;
    }
    initialEditAppliedRef.current = false;
    applyRoleGenFormSnapshot(initialEditContext.formValues);
    setSelectedHistoryPreview(null);
  }, [applyRoleGenFormSnapshot, initialEditContext, open]);

  // 历史列表首次加载完成后按真实 task_id 回填并选中记录，未命中时保持右侧空状态。
  useEffect(() => {
    if (
      !open
      || !initialEditContext
      || !roleGenHistoryInitialized
      || initialEditAppliedRef.current
    ) {
      return;
    }
    const targetItem = visibleRoleGenHistoryItems.find(
      (item) => item.taskId === initialEditContext.taskId,
    );
    if (targetItem) {
      applyRoleGenFormSnapshot(targetItem.formSnapshot);
    }
    setSelectedHistoryPreview(targetItem ?? null);
    initialEditAppliedRef.current = true;
  }, [
    applyRoleGenFormSnapshot,
    initialEditContext,
    open,
    roleGenHistoryInitialized,
    visibleRoleGenHistoryItems,
  ]);

  // 父弹框关闭时同步清空创建角色状态，避免下次打开时残留上次选择。
  useEffect(() => {
    openRef.current = open;
    if (open) return;
    resetRoleModalState();
  }, [open, resetRoleModalState]);

  // 创建角色弹窗每次打开上报一次曝光，room_id 按当前资产库入口解析。
  useEffect(() => {
    if (!open) return;
    trackRoboneoAssetRoleShow(roomId);
  }, [open, roomId]);

  // 任务创建后立即同步生成中卡片，生成成功后再次刷新后端结果图。
  useEffect(() => {
    if (!taskId || (status !== 'generating' && status !== 'succeeded')) return;
    refreshRoleGenHistory().catch(() => undefined);
  }, [refreshRoleGenHistory, status, taskId]);

  // 选中的历史任务结束轮询后同步最新结果；失败任务已从列表过滤，同时退出其 loading 预览。
  useEffect(() => {
    if (!selectedHistoryPreview?.loading) return;
    const latestItem = roleGenHistoryItems.find(
      (item) => item.taskId === selectedHistoryPreview.taskId,
    );
    if (!latestItem || latestItem.loading) return;
    setSelectedHistoryPreview(latestItem.failed ? null : latestItem);
  }, [roleGenHistoryItems, selectedHistoryPreview]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title={languageStore.smartcatTranslationText((value) => value.create)}
      width={1070}
      centered
      className={styles.modalRoot}
      zIndex={CREATE_ROLE_MODAL_Z_INDEX}
      closeIcon={<IconFont name="crossBlack" fontSize={18} color="currentColor" />}
      // 弹框关闭时主动 reset，保留挂载以避免二级弹层卸载动画抖动。
      destroyOnClose={false}
    >
      <div className={styles.content}>
        <svg className={styles.roleIconGradientDefs} width={0} height={0}>
          <defs>
            <linearGradient id="create-role-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--effects-gradient1-1, #fff)" />
              <stop offset="33%" stopColor="var(--effects-gradient1-2, #8ea8fe)" />
              <stop offset="66%" stopColor="var(--effects-gradient1-3, #f899ff)" />
              <stop offset="100%" stopColor="var(--effects-gradient1-1, #fff)" />
            </linearGradient>
          </defs>
        </svg>
        <div className={styles.body}>
          <div className={styles.formCol}>
            <RoleSettingForm
              values={values}
              onChange={handleChange}
              onOpenMaterialSelector={handleOpenMaterialSelector}
              onUploadReferenceImage={handleUploadReferenceImage}
              onHistoryUploadReference={handleOpenReferenceHistoryUpload}
              onLocalUploadMaterial={handleLocalUploadMaterial}
              onHistoryUploadMaterial={handleOpenHistoryUpload}
              onUploadControlClick={handleRoleUploadControlClick}
              uploadingReferenceImage={uploadingReferenceImage}
              uploadingMaterialCategory={uploadingMaterialCategory}
            />
          </div>
          <div className={styles.previewCol}>
            <h3 className={styles.previewTitle}>
              {languageStore.smartcatTranslationText((value) => value.character_preview)}
            </h3>
            <ThreeViewPreview
              status={displayPreviewStatus}
              images={displayPreviewImages}
              onSaveAsAsset={handleSaveImage}
              onAddToCanvas={onAddImageToCanvas ? handleAddImageToCanvas : undefined}
              addToCanvasDisabledReason={addToCanvasDisabledReason}
            />
            {generatedHistory.length > 0 && (
              <HistoryList
                items={generatedHistory}
                selectedTaskId={selectedHistoryTaskId}
                scrollToTaskId={initialEditContext
                  && selectedHistoryPreview?.taskId === initialEditContext.taskId
                  ? initialEditContext.taskId
                  : undefined}
                roomId={roomId}
                onPreview={handlePreviewHistoryItem}
                onEdit={handleEditHistoryItem}
                onSaveAsAsset={handleSaveHistoryItem}
                onDelete={handleDeleteHistoryItem}
              />
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.footerControls}>
            <RoleModelControls
              models={modelOptions}
              loading={modelOptionsLoading}
              selectedModel={selectedModel}
              selectedParameters={values.modelParameters}
              onModelChange={handleModelChange}
              onParameterChange={handleModelParameterChange}
            />
          </div>
          <div className={styles.footerActions}>
            {showCreditCost && (
              <span className={styles.creditCost}>
                {roleGenCostLoading || roleGenCost === undefined ? (
                  <IconFont name="loadingCircleBold" fontSize={16} color="currentColor" className={styles.spinningIcon} />
                ) : (
                  <RadishCostText cost={roleGenCost} discountCost={roleGenDiscountCost} />
                )}
                <img
                  src={radishIcon.src}
                  width={16}
                  height={16}
                  alt=""
                  aria-hidden="true"
                  crossOrigin="anonymous"
                />
              </span>
            )}
            <button
              type="button"
              className={`${styles.generateButton} ${creatingTask ? styles.generateButtonCreating : ''}`}
              disabled={submitDisabled}
              onClick={handleSubmit}
            >
              {creatingTask && (
                <IconFont
                  name="loadingCircleBold"
                  fontSize={16}
                  color="currentColor"
                  className={styles.spinningIcon}
                />
              )}
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
      {selectingMaterialCategory && (
        <OfficialMaterialSelectModal
          open={Boolean(selectingMaterialCategory)}
          category={selectingMaterialCategory}
          selectedMaterialId={values[`${selectingMaterialCategory}MaterialId` as keyof RoleGenFormValues] as string | undefined}
          selectedFilter={selectedMaterialFilter}
          onClose={handleCloseMaterialSelector}
          onConfirm={handleConfirmMaterial}
        />
      )}
      {historyUploadTarget && (
        <Modal
          open={Boolean(historyUploadTarget)}
          footer={null}
          closable={false}
          centered
          width={1000}
          zIndex={CREATE_ROLE_HISTORY_MODAL_Z_INDEX}
          className={styles.historyUploadModal}
          onCancel={handleCloseHistoryUpload}
          destroyOnClose
        >
          <HistoryModalContent
            setOpenAssetPopover={handleHistoryPopoverOpenChange}
            insertTarget="canvas"
            enableAddCanvasClickTrack={false}
            hideBatchManagementActions
            allowedMaterialTypes={['image']}
            assetActionLabel={languageStore.smartcatTranslationText((v) => v.confirm_upload)}
            onAssetItemClick={handleConfirmHistoryUpload}
          />
        </Modal>
      )}
    </Modal>
  );
}

export default CreateRoleModal;
